import { useEffect, useRef, useState } from "react";
import {
  CompoundDoseCurveData,
  CurveParams,
  DRCDatasetOptions,
} from "@depmap/types";
import { breadboxAPI, legacyPortalAPI, cached } from "@depmap/api";
import { TableFormattedData } from "../types";
import { fetchMetadata } from "../fetchDataHelpers";
import { getKeysByValue } from "../utils";

function buildTableData(
  viabilityAtDose: any,
  dosefMetadata: any,
  modelMetadata: any,
  aucs: Record<string, number>,
  doseCurvesResponse: CompoundDoseCurveData
): { table: TableFormattedData; orderedDoseColumns: string[] } {
  const tableLookup: Record<string, Record<string, number>> = {};
  const allDoseKeys = new Set<string>();
  const doseKeyToVal = new Map<string, number>();
  Object.entries(viabilityAtDose).forEach(([label, modelValuesRaw]) => {
    const modelValues = modelValuesRaw as Record<string, number | null>;
    Object.entries(modelValues).forEach(([model, log2Viability]) => {
      if (log2Viability !== null) {
        // wrap toPrecision(4) in the parse float and then convert back to number to avoid trailing zeros (e.g. 3.0000)
        // while still rounding long decimal points like 0.123456789 to 0.1234
        const dose = parseFloat(
          dosefMetadata.Dose[label].toPrecision(4)
        ).toString();

        const unit = dosefMetadata.DoseUnit[label];
        const doseKey = `${dose} ${unit}`;

        doseKeyToVal.set(doseKey, parseFloat(dose));
        allDoseKeys.add(doseKey);
        if (!tableLookup[model]) {
          tableLookup[model] = {};
        }
        // Transfrom log2Viability back to viability for display in the Dose Curves and Heatmap table.
        tableLookup[model][doseKey] = 2 ** log2Viability;
      }
    });
  });
  // Sort dose columns by numeric value using doseValToKey
  const orderedDoseColumns = Array.from(allDoseKeys).sort(
    (a, b) => (doseKeyToVal.get(a) ?? 0) - (doseKeyToVal.get(b) ?? 0)
  );
  // Build a lookup for curve_params by id for fast access
  const curveParamsById: Record<string, CurveParams> = {};
  if (doseCurvesResponse && Array.isArray(doseCurvesResponse.curve_params)) {
    for (const params of doseCurvesResponse.curve_params) {
      if (params.id) {
        curveParamsById[params.id] = params;
      }
    }
  }

  const table = Object.entries(tableLookup).map(([modelId, doseMap]) => {
    const row: any = {
      cellLine: modelMetadata.CellLineName[modelId],
      modelId,
      // Use toFixed(4) to match rounding of the old dose curves page
      auc: parseFloat(aucs[modelId]?.toFixed(4) ?? "NaN"),
    };
    orderedDoseColumns.forEach((doseKey) => {
      row[doseKey] = doseMap[doseKey];
    });
    // Add curve params if available
    const curveParams = curveParamsById[modelId];
    if (curveParams) {
      row.ec50 = curveParams.ec50;
      row.slope = curveParams.slope;
      row.lowerAsymptote = curveParams.lowerAsymptote;
      row.upperAsymptote = curveParams.upperAsymptote;
    }
    return row;
  });
  return { table, orderedDoseColumns };
}

export async function fetchCompoundDoseCurveData(
  compoundId: string,
  drcDatasetLabel: string,
  replicateDataset: string
): Promise<CompoundDoseCurveData | null> {
  const dapi = legacyPortalAPI;
  const curveData = await cached(dapi).getCompoundDoseCurveData!(
    compoundId,
    drcDatasetLabel,
    replicateDataset
  );

  return curveData;
}

export default function useDoseViabilityData(
  dataset: DRCDatasetOptions | null,
  compoundId: string
) {
  const [
    tableFormattedData,
    setTableFormattedData,
  ] = useState<TableFormattedData | null>(null);
  const [doseColumnNames, setDoseColumnNames] = useState<string[]>([]);
  const [
    doseCurveData,
    setDoseCurveData,
  ] = useState<CompoundDoseCurveData | null>(null);
  const [doseMin, setDoseMin] = useState<number | null>(null);
  const [doseMax, setDoseMax] = useState<number | null>(null);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const requestIdRef = useRef(0);

  useEffect(() => {
    requestIdRef.current += 1;
    const thisRequestId = requestIdRef.current;
    if (!dataset) {
      setTableFormattedData(null);
      setDoseColumnNames([]);
      setDoseCurveData(null);
      setError(false);
      setIsLoading(false);
      return;
    }
    (async () => {
      setIsLoading(true);
      setError(false);
      try {
        // Get the compound dose viability features by fetching the compound dose metadata. Will
        // be like {CompoundID: "viability_feature_id": "compound_id"}
        const doseCompoundMetadata = await fetchMetadata<{
          CompoundID: Record<string, string>;
        }>("compound_dose", null, ["CompoundID"]);

        // All of the viability features relevant to this particular compound will be the list of keys
        // with a value equal to this compoundId.
        const viabilityFeatureIds = getKeysByValue(
          doseCompoundMetadata.CompoundID,
          compoundId
        );

        if (viabilityFeatureIds.length === 0) {
          throw new Error("No viability data found.");
        }

        const [
          viabilityAtDose,
          doseMetadata,
          modelMetadata,
          aucsListResponse,
          doseCurvesResponse,
        ] = await Promise.all([
          // For getting the values for the dose viability table dose columns and the heatmap.
          cached(breadboxAPI, { persist: true }).getMatrixDatasetData(
            dataset.viability_dataset_given_id,
            {
              features: viabilityFeatureIds,
              feature_identifier: "id",
            }
          ),
          fetchMetadata<{
            Dose: Record<string, number>;
            DoseUnit: Record<string, string>;
          }>("compound_dose", viabilityFeatureIds, ["Dose", "DoseUnit"]),
          // For getting model id --> cell line name
          fetchMetadata<{ CellLineName: Record<string, string> }>(
            "depmap_model",
            null,
            ["CellLineName"]
          ),
          // For getting the AUC column of the dose viability table.
          cached(breadboxAPI, { persist: true }).getMatrixDatasetData(
            dataset.auc_dataset_given_id,
            {
              features: [compoundId],
              feature_identifier: "id",
            }
          ),
          // For getting dose curves plot data, including replicates. Curve_params are also
          // added to the dose viability table below.
          fetchCompoundDoseCurveData(
            compoundId,
            dataset.drc_dataset_label,
            dataset.replicate_dataset
          ),
        ]);

        // AUC will be an additional column added to the table
        const aucs = aucsListResponse[compoundId];

        // Build table and columns
        const { table, orderedDoseColumns } = buildTableData(
          viabilityAtDose,
          doseMetadata,
          modelMetadata,
          aucs,
          doseCurvesResponse! // For adding the dose curve params as columns to the table
        );

        // For determining the min/max of dose curve axes
        const doseValues = Array.isArray(orderedDoseColumns)
          ? orderedDoseColumns
              .map((d) => parseFloat(d.split(" ")[0]))
              .filter((n) => !Number.isNaN(n))
          : [];

        // Don't set state if the request is stale to avoid a race condition that
        // could cause incorrect data to display in the UI as a result of rapidly
        // changing props (e.g. switching between different datasets).
        if (requestIdRef.current !== thisRequestId) return;
        setDoseMin(doseValues.length > 0 ? Math.min(...doseValues) : null);
        setDoseMax(doseValues.length > 0 ? Math.max(...doseValues) : null);

        setTableFormattedData(table);
        setDoseColumnNames(orderedDoseColumns);
        setDoseCurveData(doseCurvesResponse);
        setIsLoading(false);
      } catch (e) {
        if (requestIdRef.current !== thisRequestId) return;
        window.console.error(e);
        setTableFormattedData(null);
        setDoseColumnNames([]);
        setDoseCurveData(null);
        setError(true);
        setIsLoading(false);
      }
    })();
  }, [dataset, compoundId]);

  return {
    tableFormattedData,
    doseCurveData,
    doseColumnNames,
    doseMin,
    doseMax,
    error,
    isLoading,
  };
}
